import { getFirestore, doc, setDoc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { 
  getAuth, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  sendPasswordResetEmail, 
  updateProfile, 
  updateEmail, 
  sendEmailVerification,
  fetchSignInMethodsForEmail,
} from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { initializeApp } from 'firebase/app';
import { firebaseConfig } from '../firebaseHelper';
import { getFunctions, httpsCallable } from 'firebase/functions';
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);
const functions = getFunctions(app);
export const sendEmailOtp = async (email) => {
try {
const currentUser = auth.currentUser;
if (currentUser) {
await sendEmailVerification(currentUser);
return true;
}
throw new Error('No authenticated user found');
} catch (error) {
console.error('Error sending email verification:', error);
throw error;
}
};
// Add phone number OTP function
export const sendPhoneOTP = async (phoneNumber) => {
try {
// Format phone number properly
let formattedNumber = phoneNumber;
if (!formattedNumber.startsWith('+')) {
  formattedNumber = `+${formattedNumber}`;
}

// Call Firebase function
const sendOTPFunction = httpsCallable(functions, 'sendOTP');
const result = await sendOTPFunction({ phoneNumber: formattedNumber });

return result.data;
} catch (error) {
console.error('Error sending phone OTP:', error);
throw error;
}
};
// Add phone number OTP verification function
export const verifyPhoneOTP = async (phoneNumber, otp) => {
try {
// Format phone number properly
let formattedNumber = phoneNumber;
if (!formattedNumber.startsWith('+')) {
  formattedNumber = `+${formattedNumber}`;
}

// Call Firebase function
const verifyOTPFunction = httpsCallable(functions, 'verifyOTP');
const result = await verifyOTPFunction({ 
  phoneNumber: formattedNumber,
  otp: otp
});

return result.data;
} catch (error) {
console.error('Error verifying phone OTP:', error);
throw error;
}
};

export const uploadProfilePhoto = async (file, userId) => {
  try {
    const filename = `profile_${Date.now()}_${file.name}`;
    const storageRef = ref(storage, `users/${userId}/profileMedia/${filename}`);
    
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    
    return downloadURL;
  } catch (error) {
    console.error('Error uploading profile photo:', error);
    throw error;
  }
};
export const getUserData = async (userId) => {
try {
const userDoc = await getDocs(query(collection(db, 'users'), where('userId', '==', userId)));
if (!userDoc.empty) {
return userDoc.docs[0].data();
}
throw new Error('User not found');
} catch (error) {
console.error('Error getting user data:', error);
throw error;
}
};
// Modified to support both email and phone signup
export const signUp = async (formData) => {
try {
// Create a temporary email based on phone number if phone signup
let email = formData.email;
let isPhoneSignup = false;
if (!email && formData.phoneNumber) {
  // Remove '+' and create a temporary email
  const phoneDigits = formData.phoneNumber.replace(/\+/g, '');
  email = `${phoneDigits}@mail.trillio.in`;
  isPhoneSignup = true;
}

if (!email) {
  throw new Error('Either email or phone number is required');
}

// Create user with the email/password
const result = await createUserWithEmailAndPassword(auth, email, formData.password);
const { uid } = result.user;

// Update user profile with display name
await updateProfile(result.user, { 
  displayName: formData.fullName 
});

// Only send email verification if it's a real email
if (!isPhoneSignup) {
  await sendEmailVerification(result.user);
}

// Create user data object
const signUpDate = new Date().toISOString();
const userData = {
  uid,
  userId: uid,
  fullName: formData.fullName,
  email: isPhoneSignup ? null : email, // Don't store the temp email for phone signup
  phoneNumber: formData.phoneNumber || '',
  phoneVerified: isPhoneSignup ? 'Yes' : 'No', // Phone is verified if signed up with phone
  userType: formData.userType,
  userSubType: formData.userSubType || '',
  userName: formData.userName,
  signUpDate,
  profileCoverPictureUrl: '',
  profilePictureUrl: formData.profilePictureUrl || '',
  toursCount: 0,
  showsCount: 0,
  postsCount: 0,
  bio: '',
  birthday: formData.birthday || null,
  address: '',
  city: formData.city || '',
  state: formData.state || '',
  country: formData.country || '',
  profileStatus: '',
  idVerificationStatus: 'Not Verified',
  paymentInfo: '',
  socialLinks: '',
  membershipTier: 'Free',
  profileUrl: `https://trillio.in/${formData.userName}`,
  profileUrlAlt: `https://trillio.com/${formData.userName}`,
  industry: formData.industry || '',
  currency: 'INR (₹)',
  emailVerified: isPhoneSignup ? 'No' : 'No', // Will be updated after verification
  gender: formData.gender || '',
  accountDeletionRequestedAt: null,
  isLoggedIn: true,
  startedLearningYear: formData.startedLearningYear || '',
  overallExperienceYears: formData.overallExperienceYears || null,
  startedProfessionalYear: formData.startedProfessionalYear || '',
  professionalExperienceYears: formData.professionalExperienceYears || null
};

// Add specific fields based on user type
if (formData.userType === 'Manager' || formData.userType === 'Artist Management Company') {
  userData.usersCount = 0;
} else if (formData.userType === 'Artist') {
  userData.fansCount = 0;
  if (formData.isInBand) {
    userData.bands = formData.bands || [];
  }
}

// Save user data to Firestore
const userRef = doc(db, 'users', uid);
await setDoc(userRef, userData);

return { user: result.user, userData };
} catch (error) {
console.error('Error during sign-up:', error);
throw error;
}
};
export const getUserDataWithCache = async (userId, cache) => {
const cacheEntry = cache?.get(userId);
if (cacheEntry) {
return cacheEntry.data;
}
const userData = await getUserData(userId);
cache?.set(userId, { data: userData });
return userData;
};
// Modified to support both email and phone login
export const signIn = async (loginId, password) => {
try {
let userCredential;
// Check if the login ID is a phone number
const isPhone = /^\+?\d+$/.test(loginId.replace(/\s/g, ''));

if (isPhone) {
  // Create a temporary email from phone number
  const phoneDigits = loginId.replace(/\+/g, '').replace(/\s/g, '');
  const tempEmail = `${phoneDigits}@mail.trillio.in`;
  
  // Try to sign in with the temporary email
  userCredential = await signInWithEmailAndPassword(auth, tempEmail, password);
} else {
  // Regular email login
  userCredential = await signInWithEmailAndPassword(auth, loginId, password);
}

const { uid } = userCredential.user;
const token = await userCredential.user.getIdToken();


// Get user data
const userDoc = await getDocs(query(collection(db, 'users'), where('userId', '==', uid)));
if (userDoc.empty) throw new Error('User data not found');

const userData = userDoc.docs[0].data();
const profileUrl = `https://trillio.in/${userData.userName}`;

// Update login status
await updateDoc(doc(db, 'users', uid), {
  isLoggedIn: true,
  lastLoginTime: new Date().toISOString(),
  accountDeletionRequestedAt: null,
  profileUrl
});

// Return complete user data to be cached in context
return {
  user: userCredential.user,
  userData: { ...userData, profileUrl },
  token,
  timestamp: Date.now() // For cache invalidation
};
} catch (error) {
console.error('Error during sign-in:', error);
throw error;
}
};
// Add phone verification check
export const isPhoneNumberRegistered = async (phoneNumber) => {
try {
// Format phone number consistently
let formattedNumber = phoneNumber;
if (!formattedNumber.startsWith('+')) {
  formattedNumber = `+${formattedNumber}`;
}

// Create the temporary email from phone
const phoneDigits = formattedNumber.replace(/\+/g, '');
const tempEmail = `${phoneDigits}@mail.trillio.in`;

// Check if this email exists
const methods = await fetchSignInMethodsForEmail(auth, tempEmail);
return methods.length > 0;
} catch (error) {
console.error('Error checking phone number:', error);
return false;
}
};
export const resetPassword = async (email) => {
try {
await sendPasswordResetEmail(auth, email);
return true;
} catch (error) {
console.error('Error in resetPassword:', error);
throw error;
}
};
export const updateUserEmail = async (newEmail) => {
try {
const user = auth.currentUser;
if (!user) throw new Error('No authenticated user found');
await updateEmail(user, newEmail);
const userRef = doc(db, 'users', user.uid);
await updateDoc(userRef, { email: newEmail });
return true;
} catch (error) {
console.error('Error updating email:', error);
throw error;
}
};
export const checkUsernameUniqueness = async (username) => {
try {
const usersRef = collection(db, 'users');
const querySnapshot = await getDocs(query(usersRef, where('userName', '==', username)));
return querySnapshot.empty;
} catch (error) {
console.error('Error checking username:', error);
throw error;
}
};